// Landing page for the emailed magic-link (/login/link?token=…).
// Confirming here approves the login request — the session is issued
// to the DEVICE THAT REQUESTED THE LINK (it's polling for approval),
// never to this one. The explicit confirm button also means an email
// scanner pre-fetching the URL can't approve anything by itself.

import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { MailCheck, ShieldQuestion, CheckCircle2 } from "lucide-react";
import { usePageTitle } from "../hooks/usePageTitle";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";
import LanguageToggle from "../components/LanguageToggle";
import "../styles/login.css";
import "../styles/login-link.css";

type State = "confirm" | "working" | "done" | "invalid";

const LoginLinkApprove = () => {
    usePageTitle("login");
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const token = params.get("token") ?? "";
    const [state, setState] = useState<State>(token ? "confirm" : "invalid");

    const confirm = async () => {
        if (state !== "confirm") return;
        setState("working");
        try {
            const res = await apiFetch(`/login/magic/approve`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token }),
            });
            setState(res.ok ? "done" : "invalid");
        } catch {
            // Network failure ≠ dead link — let the user try again.
            setState("confirm");
        }
    };

    return (
        <div id="login-background">
            <div id="login-box" className="animate-slideUp">
                <LanguageToggle className="login-language-toggle" />

                {(state === "confirm" || state === "working") && (
                    <>
                        <MailCheck size={40} className="login-link-page-icon" aria-hidden />
                        <h1 className="login-title">{t("login_link_approve_title")}</h1>
                        <p className="login-link-page-body">{t("login_link_approve_body")}</p>
                        <button
                            type="button"
                            className="btn btn-primary btn-full"
                            onClick={confirm}
                            disabled={state === "working"}
                        >
                            {state === "working"
                                ? t("login_link_approve_working")
                                : t("login_link_approve_confirm")}
                        </button>
                    </>
                )}

                {state === "done" && (
                    <>
                        <CheckCircle2 size={40} className="login-link-page-icon" aria-hidden />
                        <h1 className="login-title">{t("login_link_approved_title")}</h1>
                        <p className="login-link-page-body">{t("login_link_approved_body")}</p>
                    </>
                )}

                {state === "invalid" && (
                    <>
                        <ShieldQuestion size={40} className="login-link-page-icon" aria-hidden />
                        <h1 className="login-title">{t("login_link_invalid_title")}</h1>
                        <p className="login-link-page-body">{t("login_link_invalid_body")}</p>
                        <Link to="/login" className="btn btn-primary btn-full">
                            {t("login")}
                        </Link>
                    </>
                )}
            </div>

            <div className="login-footer">
                <img
                    className="login-footer-logo"
                    src="/images/jli-logo-white.svg"
                    alt="JLI Trading"
                />
            </div>
        </div>
    );
};

export default LoginLinkApprove;
